/**
 * Google tag (Ads conversion measurement) — client helpers.
 *
 * The tag only exists when NEXT_PUBLIC_GADS_ID is set, and only measures once
 * the visitor accepts the consent banner (Consent Mode v2: everything defaults
 * to denied, so before consent Google receives at most cookieless pings).
 * Conversion labels are per-action codes minted in the Ads UI; each helper
 * no-ops when its label is unset so the code can ship ahead of the labels.
 */

export const GADS_ID = process.env.NEXT_PUBLIC_GADS_ID;
export const GADS_LABELS = {
  signup: process.env.NEXT_PUBLIC_GADS_LABEL_SIGNUP,
  newsletter: process.env.NEXT_PUBLIC_GADS_LABEL_NEWSLETTER,
} as const;

const CONSENT_KEY = "rb-ads-consent";

type GtagWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

function gtag(...args: unknown[]) {
  try {
    (window as GtagWindow).gtag?.(...args);
  } catch {
    // Measurement must never break the app.
  }
}

export type ConsentChoice = "granted" | "denied" | null;

export function readConsent(): ConsentChoice {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

const CHANGE_EVENT = "rb-consent-change";

/** Subscribe to consent-choice changes (for useSyncExternalStore). */
export function onConsentChange(cb: () => void) {
  window.addEventListener(CHANGE_EVENT, cb);
  return () => window.removeEventListener(CHANGE_EVENT, cb);
}

export function grantConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, "granted");
  } catch {}
  gtag("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function denyConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, "denied");
  } catch {}
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Fire an Ads conversion event; silently a no-op without tag, label, or consent.
 * Value/currency mirror the conversion actions' configuration in the Ads UI. */
export function adsConversion(label: keyof typeof GADS_LABELS) {
  const code = GADS_LABELS[label];
  if (!GADS_ID || !code) return;
  gtag("event", "conversion", {
    send_to: `${GADS_ID}/${code}`,
    value: 1.0,
    currency: "CAD",
  });
}
